import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../config";
import CompactYouTubePlayer from "../components/CompactYouTubePlayer";
import { timeToSec, secToHMS } from "../utils/time";
import { REEL_FRAME_THEMES, DEFAULT_REEL_FRAME_THEME_ID } from "../constants/reelFrameThemes";
import {
  FaArrowLeft, FaChevronDown, FaChevronRight, FaPlus, FaTrash,
  FaPlay, FaFilm, FaCheckCircle, FaSpinner, FaExclamationTriangle, FaClock,
} from "react-icons/fa";

const ADMIN_PASSWORD = "kk26";
const AUTOSAVE_DELAY_MS = 800;

const GENRE_SUGGESTIONS = [
  "హాస్యం", "భావోద్వేగం", "భయానక", "చారిత్రిక", "పిల్లల కథ", "ప్రేమకథ", "సాంఘిక",
];

// Extracted once from performances.analysis_text — same regex the Python
// pipeline uses (pipeline/reels/analysis.py: extract_punch_lines), kept in
// sync by hand since this is JS/Python duplication of a small, stable rule.
const PUNCHLINE_SECTION_RE = /పంచ్\s*లైన్స్\s*\)?\s*:?\s*([\s\S]*?)(?:కథ\s*చెప్పిన\s*తీరు|$)/;
const QUOTED_RE = /['‘’]([^'‘’]{4,200})['‘’]/g;

function extractPunchlinesFromAnalysis(analysisText) {
  if (!analysisText) return [];
  const m = PUNCHLINE_SECTION_RE.exec(analysisText);
  if (!m) return [];
  const out = [];
  let qm;
  QUOTED_RE.lastIndex = 0;
  while ((qm = QUOTED_RE.exec(m[1])) !== null) {
    const t = qm[1].trim();
    if (t) out.push({ text: t, approx_ts_sec: null, auditory_highlight: false });
  }
  return out;
}

// ─── Small building blocks ──────────────────────────────────────────────

const SaveIndicator = ({ status }) => {
  if (!status) return null;
  if (status === "saving") return <FaSpinner className="animate-spin text-white/30 text-[11px]" title="Saving…" />;
  if (status === "saved") return <FaCheckCircle className="text-green-400/70 text-[11px]" title="Saved" />;
  if (status === "error") return <FaExclamationTriangle className="text-red-400/70 text-[11px]" title="Save failed" />;
  return null;
};

const FieldLabel = ({ children, status }) => (
  <div className="flex items-center gap-2 mb-1">
    <span className="text-xs font-semibold text-kk-gold/70">{children}</span>
    <SaveIndicator status={status} />
  </div>
);

// ─── Story start/end timestamp row (mirrors EpisodeTimestamps.js's
// TimestampSection UX: inline HH:MM:SS field + Set-from-player button) ──

const TimestampRow = ({ label, value, onChange, onSetFromPlayer, onPlay, color, saveStatus }) => {
  const sec = timeToSec(value);
  const colorMap = {
    blue: { bg: "bg-blue-500/10", border: "border-blue-500/30", btn: "bg-blue-600 hover:bg-blue-500" },
    green: { bg: "bg-green-500/10", border: "border-green-500/30", btn: "bg-green-600 hover:bg-green-500" },
  };
  const c = colorMap[color] || colorMap.blue;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-white/40 w-10">{label}</span>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="H:MM:SS"
        className={`w-24 px-2 py-1.5 ${c.bg} border ${c.border} rounded text-white text-sm font-mono text-center
          placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20`}
      />
      {sec != null && <span className="text-[10px] text-white/25 font-mono w-12">{sec}s</span>}
      <button onClick={onSetFromPlayer}
        className={`px-2.5 py-1.5 text-white text-[11px] rounded-lg font-semibold ${c.btn} transition-all active:scale-95 flex items-center gap-1`}>
        <FaClock className="text-[9px]" /> Set from player
      </button>
      {sec != null && (
        <button onClick={() => onPlay(sec)} className="p-1.5 text-kk-gold/50 hover:text-kk-gold transition-colors">
          <FaPlay className="text-[9px]" />
        </button>
      )}
      <SaveIndicator status={saveStatus} />
    </div>
  );
};

// ─── Punchlines editor ──────────────────────────────────────────────────

const PunchlinesEditor = ({ punchlines, onChange, onSeek }) => {
  const list = punchlines || [];

  const update = (i, patch) => {
    const next = list.map((p, idx) => (idx === i ? { ...p, ...patch } : p));
    onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, { text: "", approx_ts_sec: null, auditory_highlight: false }]);

  return (
    <div className="space-y-2">
      {list.map((p, i) => (
        <div key={i} className="flex items-start gap-2 bg-white/[0.02] border border-white/5 rounded-lg p-2">
          <textarea
            value={p.text || ""}
            onChange={(e) => update(i, { text: e.target.value })}
            rows={2}
            placeholder="పంచ్ లైన్..."
            className="flex-1 px-2 py-1.5 bg-white/5 border border-white/10 rounded text-sm text-kk-cream
              placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40 resize-y"
          />
          <input
            type="text"
            value={p.approx_ts_sec ?? ""}
            onChange={(e) => update(i, { approx_ts_sec: e.target.value === "" ? null : Number(e.target.value) })}
            placeholder="sec"
            className="w-16 px-2 py-1.5 bg-white/5 border border-white/10 rounded text-xs text-center font-mono
              text-kk-cream placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40"
          />
          {p.approx_ts_sec != null && (
            <button onClick={() => onSeek(p.approx_ts_sec)} title="Play"
              className="p-1.5 text-kk-gold/50 hover:text-kk-gold transition-colors">
              <FaPlay className="text-[10px]" />
            </button>
          )}
          <button onClick={() => remove(i)} title="Remove"
            className="p-1.5 text-red-400/40 hover:text-red-400 transition-colors">
            <FaTrash className="text-[10px]" />
          </button>
        </div>
      ))}
      <button onClick={add}
        className="flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-kk-gold/25 rounded-lg
          text-kk-gold/50 text-xs hover:text-kk-gold hover:border-kk-gold/40 transition-colors w-full justify-center">
        <FaPlus className="text-[9px]" /> Add punch line
      </button>
    </div>
  );
};

// ─── Highlight segments editor — shows both auto-identified (from the
// automatic pipeline) and manually-added segments, visually distinguished ──

const HighlightSegmentsEditor = ({ segments, onChange, currentTime, onSeek }) => {
  const list = segments || [];

  const update = (i, patch) => {
    const next = list.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, { start_sec: Math.floor(currentTime), end_sec: Math.floor(currentTime) + 10, label: `segment${list.length}`, source: "manual" }]);

  if (list.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-[11px] text-white/30 italic">
          No highlight segments yet — none were auto-identified for this story, or the automatic pass hasn't run yet.
        </p>
        <button onClick={add}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-kk-gold/25 rounded-lg
            text-kk-gold/50 text-xs hover:text-kk-gold hover:border-kk-gold/40 transition-colors w-full justify-center">
          <FaPlus className="text-[9px]" /> Add segment (at current player time)
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {list.map((s, i) => {
        const dur = (s.end_sec != null && s.start_sec != null) ? s.end_sec - s.start_sec : null;
        const isAuto = s.source === "auto";
        return (
          <div key={i} className={`flex items-center gap-2 border rounded-lg p-2 flex-wrap
            ${isAuto ? "bg-cyan-500/[0.04] border-cyan-500/15" : "bg-white/[0.02] border-white/5"}`}>
            <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wide font-semibold
              ${isAuto ? "bg-cyan-500/15 text-cyan-300" : "bg-purple-500/15 text-purple-300"}`}>
              {isAuto ? "auto" : "manual"}
            </span>
            <input
              type="text"
              value={s.label || ""}
              onChange={(e) => update(i, { label: e.target.value })}
              placeholder="label"
              className="w-24 px-2 py-1.5 bg-white/5 border border-white/10 rounded text-xs text-kk-cream
                placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40"
            />
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={s.start_sec ?? ""}
                onChange={(e) => update(i, { start_sec: Number(e.target.value) })}
                className="w-20 px-2 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-center font-mono text-kk-cream"
              />
              <button onClick={() => update(i, { start_sec: Math.floor(currentTime) })}
                className="px-2 py-1.5 bg-blue-600 hover:bg-blue-500 rounded text-white text-[10px] font-semibold">
                Set Start
              </button>
            </div>
            <span className="text-white/20 text-xs">→</span>
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={s.end_sec ?? ""}
                onChange={(e) => update(i, { end_sec: Number(e.target.value) })}
                className="w-20 px-2 py-1.5 bg-green-500/10 border border-green-500/30 rounded text-xs text-center font-mono text-kk-cream"
              />
              <button onClick={() => update(i, { end_sec: Math.floor(currentTime) })}
                className="px-2 py-1.5 bg-green-600 hover:bg-green-500 rounded text-white text-[10px] font-semibold">
                Set End
              </button>
            </div>
            {dur != null && (
              <span className="text-[10px] text-white/30 font-mono">{dur}s</span>
            )}
            <button onClick={() => onSeek(s.start_sec)} title="Play from start"
              className="p-1.5 text-kk-gold/50 hover:text-kk-gold transition-colors ml-auto">
              <FaPlay className="text-[10px]" />
            </button>
            <button onClick={() => remove(i)} title="Remove segment"
              className="p-1.5 text-red-400/40 hover:text-red-400 transition-colors">
              <FaTrash className="text-[10px]" />
            </button>
          </div>
        );
      })}
      <button onClick={add}
        className="flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-kk-gold/25 rounded-lg
          text-kk-gold/50 text-xs hover:text-kk-gold hover:border-kk-gold/40 transition-colors w-full justify-center">
        <FaPlus className="text-[9px]" /> Add segment (at current player time)
      </button>
    </div>
  );
};

// ─── Frame theme picker ─────────────────────────────────────────────────

const FrameThemePicker = ({ value, onChange }) => (
  <div className="flex items-center gap-2 flex-wrap">
    {REEL_FRAME_THEMES.map((t) => {
      const selected = t.id === (value || DEFAULT_REEL_FRAME_THEME_ID);
      return (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          title={t.tagline}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs transition-all
            ${selected ? "border-kk-gold bg-kk-gold/10 text-kk-gold" : "border-white/10 text-white/50 hover:border-white/25"}`}
        >
          <span className="w-3 h-3 rounded-full border border-white/20" style={{ background: t.swatch }} />
          {t.name}
        </button>
      );
    })}
  </div>
);

// ─── Story card ──────────────────────────────────────────────────────────

const StoryCard = ({ perf, story, writerName, storyReel, onSaveField, onGenerateReel, onSeek, currentTime, saveStatus }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [showText, setShowText] = useState(false); // transcript + analysis hidden by default

  const [title, setTitle] = useState(story?.title_telugu || "");
  const [isAuthor, setIsAuthor] = useState(perf.is_author !== false);
  const [writer, setWriter] = useState(writerName || "");
  const [storyStart, setStoryStart] = useState(perf.story_start_time || secToHMS(perf.story_start_sec));
  const [storyEnd, setStoryEnd] = useState(perf.story_end_time || secToHMS(perf.story_end_sec));
  const [transcript, setTranscript] = useState(perf.transcript || "");
  const [analysisText, setAnalysisText] = useState(perf.analysis_text || "");
  const [genre, setGenre] = useState(story?.genre || "");
  const [punchlines, setPunchlines] = useState(
    (story?.punchlines && story.punchlines.length > 0)
      ? story.punchlines
      : extractPunchlinesFromAnalysis(perf.analysis_text)
  );
  const [segments, setSegments] = useState(storyReel?.highlight_segments || []);
  const [frameTheme, setFrameTheme] = useState(storyReel?.frame_theme || DEFAULT_REEL_FRAME_THEME_ID);
  const [generateMsg, setGenerateMsg] = useState("");

  const displayTitle = title || `(untitled story — performance ${perf.performance_id})`;
  const keyFor = (field) => `${perf.performance_id}:${field}`;

  const savePerf = (field, value) =>
    onSaveField(keyFor(field), null, { table: "performances", match: { performance_id: perf.performance_id }, patch: { [field]: value } });
  const saveStory = (field, value) =>
    story?.story_id && onSaveField(keyFor(field), null, { table: "stories", match: { story_id: story.story_id }, patch: { [field]: value } });

  const handleGenerate = async () => {
    if (!segments.length) {
      setGenerateMsg("Add at least one highlight segment first.");
      return;
    }
    setGenerateMsg("Queuing…");
    const result = await onGenerateReel(story?.story_id, perf.episode_id, segments, frameTheme);
    setGenerateMsg(result.ok ? "Queued — run the local worker to render." : `Failed: ${result.error}`);
  };

  return (
    <div className="border border-kk-gold/15 rounded-xl overflow-hidden bg-white/[0.02]">
      <button onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors text-left">
        <span className="text-kk-gold/60 text-xs">{collapsed ? <FaChevronRight /> : <FaChevronDown />}</span>
        <div className="flex-1 min-w-0">
          <span className="font-krushna text-kk-cream text-base">"{displayTitle}"</span>
        </div>
        {storyReel?.status && (
          <span className="text-[10px] px-2 py-0.5 rounded bg-kk-gold/10 text-kk-gold/70">{storyReel.status}</span>
        )}
      </button>

      {!collapsed && (
        <div className="px-4 pb-4 space-y-4 border-t border-white/5 pt-3">
          <div>
            <FieldLabel status={saveStatus[keyFor("title_telugu")]}>Story name</FieldLabel>
            <input
              type="text"
              value={title}
              onChange={(e) => { setTitle(e.target.value); saveStory("title_telugu", e.target.value); }}
              placeholder="కథ పేరు..."
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-kk-cream font-krushna
                placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40"
            />
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer">
              <input
                type="checkbox"
                checked={isAuthor}
                onChange={(e) => {
                  const v = e.target.checked;
                  setIsAuthor(v);
                  savePerf("is_author", v);
                  if (v) { setWriter(""); saveStory("writer_id", null); }
                }}
                className="accent-kk-gold"
              />
              Storyteller wrote this themselves
            </label>
            {!isAuthor && (
              <div className="flex-1 min-w-[200px]">
                <FieldLabel status={saveStatus[keyFor("writer")]}>Writer</FieldLabel>
                <input
                  type="text"
                  value={writer}
                  onChange={(e) => {
                    setWriter(e.target.value);
                    if (story?.story_id) {
                      onSaveField(keyFor("writer"), null, {
                        table: "writer_lookup", story_id: story.story_id, name: e.target.value,
                      });
                    }
                  }}
                  placeholder="రచయిత పేరు..."
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-kk-cream
                    placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40"
                />
              </div>
            )}
          </div>

          <div className="bg-white/[0.02] border border-white/5 rounded-lg p-3 space-y-2">
            <FieldLabel>Story start / end (full telling, not the reel highlights)</FieldLabel>
            <TimestampRow label="Start" value={storyStart} color="blue" onPlay={onSeek}
              saveStatus={saveStatus[keyFor("story_start_time")]}
              onChange={(v) => setStoryStart(v)}
              onSetFromPlayer={() => {
                const v = secToHMS(currentTime);
                setStoryStart(v);
                savePerf("story_start_time", v);
                savePerf("story_start_sec", timeToSec(v));
              }}
            />
            <TimestampRow label="End" value={storyEnd} color="green" onPlay={onSeek}
              saveStatus={saveStatus[keyFor("story_end_time")]}
              onChange={(v) => setStoryEnd(v)}
              onSetFromPlayer={() => {
                const v = secToHMS(currentTime);
                setStoryEnd(v);
                savePerf("story_end_time", v);
                savePerf("story_end_sec", timeToSec(v));
              }}
            />
          </div>

          <div>
            <button
              type="button"
              onClick={() => setShowText((v) => !v)}
              className="flex items-center gap-2 text-xs font-semibold text-kk-gold/70 hover:text-kk-gold transition-colors"
            >
              {showText ? <FaChevronDown className="text-[10px]" /> : <FaChevronRight className="text-[10px]" />}
              Transcript &amp; Literary Analysis
              <span className="text-[10px] text-white/25 font-normal">(hidden by default — click to show)</span>
            </button>
          </div>

          {showText && (
            <>
              <div>
                <FieldLabel status={saveStatus[keyFor("transcript")]}>Transcript</FieldLabel>
                <textarea
                  value={transcript}
                  onChange={(e) => { setTranscript(e.target.value); savePerf("transcript", e.target.value); }}
                  rows={5}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-kk-cream
                    placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40 resize-y font-mono"
                />
              </div>

              <div>
                <FieldLabel status={saveStatus[keyFor("analysis_text")]}>Literary analysis</FieldLabel>
                <textarea
                  value={analysisText}
                  onChange={(e) => { setAnalysisText(e.target.value); savePerf("analysis_text", e.target.value); }}
                  rows={6}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-kk-cream
                    placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40 resize-y font-mono"
                />
              </div>
            </>
          )}

          <div>
            <FieldLabel status={saveStatus[keyFor("genre")]}>Genre</FieldLabel>
            <input
              list="genre-suggestions"
              value={genre}
              onChange={(e) => { setGenre(e.target.value); saveStory("genre", e.target.value); }}
              placeholder="హాస్యం, భయానక, ..."
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-kk-cream
                placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/40"
            />
            <datalist id="genre-suggestions">
              {GENRE_SUGGESTIONS.map((g) => <option key={g} value={g} />)}
            </datalist>
          </div>

          <div>
            <FieldLabel status={saveStatus[keyFor("punchlines")]}>Punch lines</FieldLabel>
            <PunchlinesEditor
              punchlines={punchlines}
              onSeek={onSeek}
              onChange={(next) => { setPunchlines(next); saveStory("punchlines", next); }}
            />
          </div>

          <div>
            <FieldLabel status={saveStatus[keyFor("segments")]}>Highlight segments (for the reel)</FieldLabel>
            <HighlightSegmentsEditor
              segments={segments}
              currentTime={currentTime}
              onSeek={onSeek}
              onChange={(next) => {
                setSegments(next);
                onSaveField(keyFor("segments"), null, {
                  table: "story_reels", storyReel, story_id: story?.story_id, episode_id: perf.episode_id,
                  patch: { highlight_segments: next },
                });
              }}
            />
          </div>

          <div>
            <FieldLabel>Reel frame theme</FieldLabel>
            <FrameThemePicker
              value={frameTheme}
              onChange={(id) => {
                setFrameTheme(id);
                onSaveField(keyFor("frame_theme"), null, {
                  table: "story_reels", storyReel, story_id: story?.story_id, episode_id: perf.episode_id,
                  patch: { frame_theme: id },
                });
              }}
            />
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={handleGenerate}
              disabled={!story?.story_id}
              className="flex items-center gap-2 px-4 py-2 bg-kk-gold text-kk-ink rounded-lg font-semibold text-sm
                hover:bg-kk-amber active:scale-95 transition-all disabled:opacity-40"
            >
              <FaFilm className="text-xs" /> Generate Reel
            </button>
            {generateMsg && <span className="text-xs text-white/50">{generateMsg}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// ─── Main Component ──────────────────────────────────────
// ═══════════════════════════════════════════════════════════

const EpisodeMetadataEditor = () => {
  const { episodeId } = useParams();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [episode, setEpisode] = useState(null);
  const [performances, setPerformances] = useState([]);
  const [stories, setStories] = useState({});
  const [writers, setWriters] = useState({}); // writer_id -> name_telugu
  const [storyReels, setStoryReels] = useState({}); // story_id -> latest row
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [currentTime, setCurrentTime] = useState(0);
  const playerRef = useRef(null);

  const [saveStatus, setSaveStatus] = useState({});
  const saveTimers = useRef({});
  // story_id -> reel_id, once we've lazily created a story_reels row for a
  // story that didn't have one yet.
  const reelIdByStory = useRef({});

  const [activeTab, setActiveTab] = useState(null);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) setIsAuthenticated(true);
    else setErrorMsg("Incorrect password.");
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setNotFound(false);
    try {
      const { data: ep, error: epErr } = await supabase.from("episodes").select("*").eq("episode_id", episodeId).single();
      if (epErr || !ep) { setNotFound(true); return; }
      setEpisode(ep);

      const { data: perfData } = await supabase
        .from("performances")
        .select("*")
        .eq("episode_id", episodeId)
        .not("story_id", "is", null)
        .order("story_start_sec", { ascending: true, nullsFirst: false });
      const perfs = perfData || [];
      setPerformances(perfs);

      const storyIds = [...new Set(perfs.map((p) => p.story_id).filter(Boolean))];
      if (storyIds.length > 0) {
        const { data: storyData } = await supabase.from("stories").select("*").in("story_id", storyIds);
        const map = {};
        (storyData || []).forEach((s) => { map[s.story_id] = s; });
        setStories(map);

        const writerIds = [...new Set((storyData || []).map((s) => s.writer_id).filter(Boolean))];
        if (writerIds.length > 0) {
          const { data: writerData } = await supabase.from("writers").select("writer_id,name_telugu").in("writer_id", writerIds);
          const wmap = {};
          (writerData || []).forEach((w) => { wmap[w.writer_id] = w.name_telugu; });
          setWriters(wmap);
        }

        const { data: reelData } = await supabase
          .from("story_reels")
          .select("*")
          .in("story_id", storyIds)
          .order("created_at", { ascending: false });
        const reelMap = {};
        (reelData || []).forEach((r) => {
          if (!reelMap[r.story_id]) reelMap[r.story_id] = r; // first hit = most recent (already ordered desc)
        });
        setStoryReels(reelMap);
        Object.entries(reelMap).forEach(([sid, r]) => { reelIdByStory.current[sid] = r.reel_id; });
      }
    } catch (err) {
      console.error(err);
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  }, [episodeId]);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated, fetchData]);

  const onSeek = useCallback((sec) => playerRef.current?.playFrom(sec), []);

  // ─── Group performances by storyteller, one tab per storyteller ───
  const groups = useMemo(() => {
    const map = new Map();
    performances.forEach((p) => {
      const key = p.storyteller_id || p.storyteller_name_telugu || `perf_${p.performance_id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          telugu: p.storyteller_name_telugu || "",
          english: p.storyteller_name_english || "",
          storyteller_id: p.storyteller_id,
          perfs: [],
          minStart: p.story_start_sec ?? Infinity,
        });
      }
      const g = map.get(key);
      g.perfs.push(p);
      g.minStart = Math.min(g.minStart, p.story_start_sec ?? Infinity);
    });
    return [...map.values()].sort((a, b) => a.minStart - b.minStart);
  }, [performances]);

  useEffect(() => {
    if (groups.length > 0 && (activeTab == null || !groups.some((g) => g.key === activeTab))) {
      setActiveTab(groups[0].key);
    }
  }, [groups, activeTab]);

  // Update the existing story_reels row for this story, or create one on
  // first write — story_reels has no unique constraint on story_id (a
  // story can have multiple reel attempts over time), so "the row for this
  // editing session" is tracked client-side via reelIdByStory once created.
  const upsertStoryReel = useCallback(async (storyId, episodeId, patch) => {
    const existingId = reelIdByStory.current[storyId];
    if (existingId) {
      const { data, error } = await supabase.from("story_reels").update(patch).eq("reel_id", existingId).select().single();
      if (error) throw error;
      setStoryReels((prev) => ({ ...prev, [storyId]: data }));
      return data;
    }
    const { data, error } = await supabase.from("story_reels").insert({
      story_id: storyId, episode_id: episodeId,
      highlight_segments: [], frame_theme: DEFAULT_REEL_FRAME_THEME_ID,
      ...patch,
    }).select().single();
    if (error) throw error;
    reelIdByStory.current[storyId] = data.reel_id;
    setStoryReels((prev) => ({ ...prev, [storyId]: data }));
    return data;
  }, []);

  // Find-or-create a writer by Telugu name, then point stories.writer_id at it.
  const lookupOrCreateWriter = useCallback(async (storyId, name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) {
      const { error } = await supabase.from("stories").update({ writer_id: null }).eq("story_id", storyId);
      if (error) throw error;
      return;
    }
    const { data: existing, error: findErr } = await supabase
      .from("writers").select("writer_id,name_telugu").eq("name_telugu", trimmed).maybeSingle();
    if (findErr) throw findErr;
    let writerId = existing?.writer_id;
    if (!writerId) {
      const { data: created, error: createErr } = await supabase
        .from("writers").insert({ name_telugu: trimmed }).select().single();
      if (createErr) throw createErr;
      writerId = created.writer_id;
    }
    setWriters((prev) => ({ ...prev, [writerId]: trimmed }));
    const { error: linkErr } = await supabase.from("stories").update({ writer_id: writerId }).eq("story_id", storyId);
    if (linkErr) throw linkErr;
  }, []);

  const onSaveField = useCallback((key, immediateFn, spec) => {
    return new Promise((resolve) => {
      setSaveStatus((s) => ({ ...s, [key]: "saving" }));
      clearTimeout(saveTimers.current[key]);
      saveTimers.current[key] = setTimeout(async () => {
        try {
          if (immediateFn) {
            await immediateFn();
          } else if (spec.table === "story_reels") {
            await upsertStoryReel(spec.story_id, spec.episode_id, spec.patch);
          } else if (spec.table === "writer_lookup") {
            await lookupOrCreateWriter(spec.story_id, spec.name);
          } else {
            const [[col, val]] = Object.entries(spec.match);
            const { error } = await supabase.from(spec.table).update(spec.patch).eq(col, val);
            if (error) throw error;
          }
          setSaveStatus((s) => ({ ...s, [key]: "saved" }));
          setTimeout(() => setSaveStatus((s) => ({ ...s, [key]: null })), 1500);
          resolve({ ok: true });
        } catch (err) {
          console.error(`Save failed for ${key}:`, err);
          setSaveStatus((s) => ({ ...s, [key]: "error" }));
          resolve({ ok: false, error: err.message });
        }
      }, AUTOSAVE_DELAY_MS);
    });
  }, [upsertStoryReel, lookupOrCreateWriter]);

  // Generate Reel — applied immediately (not debounced): flips this
  // story's story_reels row to status='pending' with the current segments
  // + frame theme, for pipeline/reels/job_worker.py to pick up.
  const onGenerateReel = useCallback(async (storyId, episodeId, segments, frameTheme) => {
    if (!storyId) return { ok: false, error: "No story_id linked to this performance." };
    try {
      await upsertStoryReel(storyId, episodeId, {
        highlight_segments: segments, frame_theme: frameTheme, status: "pending", error_message: null,
      });
      return { ok: true };
    } catch (err) {
      console.error("Generate Reel failed:", err);
      return { ok: false, error: err.message };
    }
  }, [upsertStoryReel]);

  // "+ Add Story" — for a storyteller who told more than one story but only
  // has one auto-detected performance row (e.g. Phani told 2 stories under
  // one performance). Creates a linked stories + performances row together
  // so every editable field (title, genre, punchlines, ...) works right away.
  const handleAddStory = useCallback(async (group) => {
    try {
      const { data: newStory, error: storyErr } = await supabase
        .from("stories").insert({ title_telugu: "" }).select().single();
      if (storyErr) throw storyErr;

      const { data: newPerf, error: perfErr } = await supabase
        .from("performances")
        .insert({
          episode_id: Number(episodeId),
          storyteller_id: group.storyteller_id || null,
          storyteller_name_telugu: group.telugu || null,
          storyteller_name_english: group.english || null,
          is_author: true,
          story_id: newStory.story_id,
        })
        .select().single();
      if (perfErr) throw perfErr;

      setStories((prev) => ({ ...prev, [newStory.story_id]: newStory }));
      setPerformances((prev) => [...prev, newPerf]);
    } catch (err) {
      console.error("Add Story failed:", err);
      window.alert(`Could not add story: ${err.message}`);
    }
  }, [episodeId]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-kk-ink flex items-center justify-center">
        <div className="bg-white/5 border border-kk-gold/20 rounded-xl p-8 max-w-md w-full">
          <h1 className="font-krushna text-kk-gold text-2xl mb-6 text-center">Episode Metadata Editor</h1>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full px-4 py-3 bg-white/5 border border-kk-gold/20 rounded-lg text-white mb-4
                focus:ring-2 focus:ring-kk-gold focus:border-transparent placeholder:text-white/30"
            />
            <button type="submit" className="w-full bg-kk-gold text-kk-ink py-3 rounded-lg hover:bg-kk-amber font-semibold transition-colors">
              Login
            </button>
            {errorMsg && <p className="text-red-400 text-sm mt-2">{errorMsg}</p>}
          </form>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-kk-ink flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-kk-gold/30 border-t-kk-gold rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-kk-ink flex flex-col items-center justify-center text-center px-6">
        <h1 className="text-2xl font-bold text-kk-cream mb-4">Episode Not Found</h1>
        <Link to="/episode-creator" className="px-6 py-2 bg-kk-gold text-kk-ink rounded-full font-semibold hover:bg-kk-amber">
          Back to Episodes
        </Link>
      </div>
    );
  }

  const videoId = episode?.video_id || episode?.youtube_broadcast_id;
  const activeGroup = groups.find((g) => g.key === activeTab);

  return (
    <div className="min-h-screen bg-kk-ink text-white">
      <div className="sticky top-0 z-30 bg-kk-ink border-b border-kk-gold/10">
        <div className="bg-kk-ink/95 border-b border-kk-gold/5 px-4 py-2">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <Link to="/episode-creator" className="flex items-center gap-2 text-kk-gold/70 hover:text-kk-gold text-sm transition-colors">
              <FaArrowLeft className="text-xs" /> Episodes
            </Link>
            <span className="text-white/20 text-xs font-mono">Ep {episode.episode_id} — Metadata Editor</span>
          </div>
        </div>
        {videoId && (
          <CompactYouTubePlayer ref={playerRef} videoId={videoId} onTimeUpdate={setCurrentTime} sticky={false} />
        )}
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-4 pb-2">
        <h1 className="font-krushna text-kk-cream text-lg leading-tight mb-1">
          {episode.title || `Episode ${episode.episode_id}`}
        </h1>
        <p className="text-xs text-white/40">
          {groups.length} storyteller(s), {performances.length} story performance(s) — edits autosave a moment after you stop typing.
        </p>
      </div>

      {groups.length === 0 ? (
        <div className="text-center py-12 text-white/30">
          <p>No story performances (with a linked story_id) found for this episode.</p>
        </div>
      ) : (
        <>
          {/* ═══ TABS ═══ */}
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center gap-1 overflow-x-auto border-b border-white/10 pb-px">
              {groups.map((g) => {
                const selected = g.key === activeTab;
                return (
                  <button
                    key={g.key}
                    onClick={() => setActiveTab(g.key)}
                    className={`px-4 py-2.5 text-sm font-krushna whitespace-nowrap border-b-2 transition-colors
                      ${selected
                        ? "border-kk-gold text-kk-gold"
                        : "border-transparent text-white/50 hover:text-white/80"}`}
                  >
                    {g.telugu || g.english}
                    {g.perfs.length > 1 && (
                      <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-white/50">{g.perfs.length}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ═══ ACTIVE TAB CONTENT ═══ */}
          <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
            {activeGroup && activeGroup.perfs.map((perf) => (
              <StoryCard
                key={perf.performance_id}
                perf={perf}
                story={stories[perf.story_id]}
                writerName={writers[stories[perf.story_id]?.writer_id]}
                storyReel={storyReels[perf.story_id]}
                onSaveField={onSaveField}
                onGenerateReel={onGenerateReel}
                onSeek={onSeek}
                currentTime={currentTime}
                saveStatus={saveStatus}
              />
            ))}

            {activeGroup && (
              <button
                onClick={() => handleAddStory(activeGroup)}
                className="flex items-center gap-2 px-3 py-2.5 border border-dashed border-kk-gold/25 rounded-lg
                  text-kk-gold/60 text-sm hover:text-kk-gold hover:border-kk-gold/40 transition-colors w-full justify-center"
              >
                <FaPlus className="text-[10px]" /> Add another story for {activeGroup.telugu || activeGroup.english}
                <span className="text-[11px] text-white/30">(not auto-detected — e.g. told a 2nd story)</span>
              </button>
            )}
          </div>
        </>
      )}

      <div className="h-20" />
    </div>
  );
};

export default EpisodeMetadataEditor;
